import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Activity, BookOpen, X, ChevronLeft, Lightbulb } from "lucide-react";
import "./LoadingScreen.css";
import Tour from "reactour";
import { purewebContext } from "../../context/PurewebContext";
import { Tooltip } from "@mui/material";
import { API_BASE_URL as _BASE } from "../../config";

const API_BASE_URL = `${_BASE}/tutorials`;

const TIPS = [
  { src: "/tips/TT1_Scenery.gif",     caption: "You can replace scenery by selecting and dragging in a new object to replace it. This is great for fast iteration." },
  { src: "/tips/TT2_Position.gif",    caption: "Drag in the Image Volume to add an image across many objects. This can achieve an LED glow, printed material look or projection." },
  { src: "/tips/TT3_ImageVolume.gif", caption: "Drag & Draw objects can be quickly positioned in default positions. This places objects into common positions quickly." },
  { src: "/tips/TT4_CreateModel.gif", caption: "You can import 3D models or create 3D models from images. This gives you maximum flexibility. You can also enhance your renderings and create videos with prompts." },
];

// ── Connection progress ──────────────────────────────────────────────────────
// The percentage is a time-based estimate, because nothing in the connection reports
// how far through it is — but it is BOUNDED BY REAL STATE, which is what keeps it
// roughly truthful. Each phase owns a slice of 0–100 and the number eases toward its
// ceiling without reaching it, so it always keeps moving, jumps forward when the
// connection genuinely advances, and can never run ahead into a phase we haven't
// reached. "Connecting" therefore tops out in the sixties no matter how long it takes.
//
// That bound is the whole point. Two earlier versions had none and both lied:
//   1. A plain timer that hit its 95% cap in ~20s and sat there, so every connection
//      looked nearly finished seconds after starting.
//   2. A curve driven by the COUNT of Arcware signalling messages — which measures how
//      talkative the server is, not progress. A chatty connection reached 91% while
//      still at the signalling step; one that got no messages sat at 7% for a minute.
//
// Slices are sized from measured timings: provisioning ("connecting") is by far the
// longest leg at ~45s, so it gets the widest band.
//
// NOTE ON LABELS: only phases 0 and 3 are ever displayed. Phase 0 is pre-connect, where
// nothing has been sent to Arcware yet and there is no external status to report, so the
// words are necessarily ours. Phase 3 is our own terminal state, which Arcware has no
// message for. In between, the status line shows Arcware's signalling messages verbatim
// (see statusLabel below) — these two labels are retained only as band names.
const PHASES = [
  // Nothing has been sent yet: the SDK is still being built (Arcware credentials are
  // fetched at runtime). Distinguishing this from "connecting" matters — it's the
  // difference between "we haven't asked yet" and "we asked and got no answer".
  { label: "Starting your session...", from: 0, to: 8 },
  { label: "Connecting to the server…", from: 8, to: 68 },
  // Video is live, so the instance exists; only UE's own startup is left.
  { label: "Starting the application…", from: 68, to: 95 },
  { label: "Ready to launch", from: 100, to: 100 },
];

// Fraction of the gap to the phase ceiling covered per tick. Asymptotic, so the bar is
// always still moving — a slow phase reads as slow rather than frozen. At a 400ms tick
// this gives a ~20s time constant, which suits the ~45s provisioning leg.
const PROGRESS_EASE = 0.02;

function getPhaseIndex(canSkipVideo, streamStatus) {
  if (canSkipVideo) return 3;
  if (streamStatus === "streaming") return 2;
  if (streamStatus && streamStatus !== "idle") return 1;
  return 0;
}

function getNextTipIndex(current, length) {
  if (length <= 1) return 0;
  let next;
  do { next = Math.floor(Math.random() * length); } while (next === current);
  return next;
}

const TutorialModal = ({
  tutorial,
  onClose,
  setShowExitMessage,
  showExitMessage,
  handleSkip,
  canSkipVideo,
  progressFill,
  progressLabel,
  showProgress = false,
}) => {
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape" && onClose) {
        onClose(event, "escapeKeyDown");
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!tutorial) return null;

  return (
    <div className="tutorial-modal-overlay" onClick={onClose}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tutorial-modal-header">
          <h4>{tutorial.title}</h4>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="tutorial-modal-content">
          <iframe
            src={tutorial.link}
            title={tutorial.title}
            width="100%"
            height="100%"
            allowFullScreen
            allow="clipboard-write"
            frameBorder="0"
          ></iframe>

          {showExitMessage && (
            <div className="fullscreen-notification">
              <div className="notification-content">
                <span>
                  Application loaded successfully! Exiting fullscreen mode.
                </span>
                <button
                  onClick={() => {
                    setShowExitMessage(false);
                  }}
                  className="notification-button"
                >
                  Ok
                </button>
              </div>
            </div>
          )}

          {canSkipVideo && !showExitMessage && (
            <div className="fullscreen-notification">
              <div className="notification-content">
                <span>Application is ready!</span>
                <button onClick={handleSkip} className="notification-button">
                  Continue
                </button>
              </div>
            </div>
          )}

          {showProgress && (
            <div className="loading-progress modal-progress">
              <div className="progress-info">
                <div className="status-indicator">
                  <Activity
                    size={16}
                    className={`loading-icon ${canSkipVideo ? "completed" : "animating"
                      }`}
                  />
                  <span>{progressLabel}</span>
                </div>
                <span className="percentage">{Math.floor(progressFill)}%</span>
              </div>
              <div className="progress-track">
                <div
                  className={`progress-bar ${canSkipVideo ? "" : "working"}`}
                  style={{ width: `${progressFill}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function LoadingScreen({
  canSkipVideo,
  handleSkip,
  displayOnly,
  arcwareMessages = [],
  loginPending = false,
  streamStatus,
}) {
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [viewMode, setViewMode] = useState("choice"); // 'choice' | 'tutorials' | 'tips'
  const [currentTipIndex, setCurrentTipIndex] = useState(() => Math.floor(Math.random() * TIPS.length));
  const [showTutorialPanel, setShowTutorialPanel] = useState(false);
  const [showExitMessage, setShowExitMessage] = useState(false);
  const [isLoadingTutorials, setIsLoadingTutorials] = useState(false);
  const [tutorialsError, setTutorialsError] = useState(null);
  const { tutorials, setTutorials, hasLoggedInOnce, startLoadingTimer, loadingTimeLeft } =
    useContext(purewebContext);
  const [isTutorialsWalkthroughVisible, setIsTutorialsWalkthroughVisible] =
    useState(
      localStorage.getItem("hasSeenTutorialBefore") !== "true"
    );
  // Arcware's newest signalling status, verbatim. We used to filter these — skipping
  // "… successfully." completions to surface the newest in-progress line instead — but
  // that made us the editor of someone else's status feed, and a filtered feed can't be
  // trusted against what Arcware support sees on their side. Newest is newest.
  const latestArcwareText = arcwareMessages[arcwareMessages.length - 1]?.text;

  const phaseIndex = getPhaseIndex(canSkipVideo, streamStatus);
  const phase = PHASES[phaseIndex];

  // The one status line, and the rule behind it: before we've called connect() the
  // status is ours to give, because nothing external is happening yet and silence there
  // is what the indefinite "Starting your session..." hang looked like. From connect()
  // onward it is Arcware's, unedited.
  //
  // The one addition is a floor for the stretch where Arcware has sent NOTHING yet —
  // between connect() and its first signalling message the line rendered empty. Filling
  // an empty line is not overriding a message: this applies only while no Arcware text
  // exists at all, and the first real one replaces it permanently. It deliberately does
  // NOT fall back once messages have started, because a fallback there would hide the
  // real status at exactly the moments the connection has gone quiet.
  const preConnect = !streamStatus || streamStatus === "idle";
  const statusLabel =
    canSkipVideo || preConnect ? phase.label : latestArcwareText ?? PHASES[0].label;

  useEffect(() => {
    if (displayOnly) {
      return;
    }

    if (!hasLoggedInOnce) {
      startLoadingTimer();
    }
  }, [displayOnly, hasLoggedInOnce]);


  useEffect(() => {
    if (tutorials?.length > 0) {
      return;
    }
    const fetchTutorials = async () => {
      setIsLoadingTutorials(true);
      setTutorialsError(null);
      try {
        const response = await axios.get(API_BASE_URL);
        setTutorials(response.data);
      } catch (err) {
        setTutorialsError(
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load tutorials"
        );
      } finally {
        setIsLoadingTutorials(false);
      }
    };

    fetchTutorials();
  }, [tutorials]);

  useEffect(() => {
    if (canSkipVideo) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
        setShowExitMessage(true);
        setTimeout(() => setShowExitMessage(false), 3000);
      }
    }
  }, [canSkipVideo]);

  // Entering a phase pulls the bar up to that phase's floor, so a real advance shows as
  // an immediate jump rather than waiting for the ease to catch up. Never downwards:
  // the bar only retreats when the connection itself restarts (below).
  useEffect(() => {
    setLoadingProgress((prev) => Math.max(prev, phase.from));
  }, [phaseIndex, phase.from]);

  useEffect(() => {
    if (displayOnly) return undefined;
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        // A ceiling below the bar means the connection went backwards (a retry drops us
        // to an earlier phase) — follow it down rather than hold a stale figure.
        if (prev > phase.to) return phase.to;
        return prev + (phase.to - prev) * PROGRESS_EASE;
      });
    }, 400);
    return () => clearInterval(interval);
  }, [displayOnly, phase.to]);


  if (displayOnly) {
    return (
      <>
        <Tour
          steps={[
            {
              selector: ".render-menu-toggle-2",
              content: "You can access all essential options from here for a smoother experience!",
            },
          ]}
          isOpen={isTutorialsWalkthroughVisible}
          onRequestClose={() => {
            setIsTutorialsWalkthroughVisible(false);
            localStorage.setItem("hasSeenTutorialBefore", "true");
          }}
        />
        <Tooltip title="You can access tutorials here!" placement="right">
          <div
            className={`tutorial-fab ${showTutorialPanel ? "active" : ""}`}
            onClick={() => setShowTutorialPanel(!showTutorialPanel)}
            id="tutorial-fab"
          >
            <BookOpen size={20} />
          </div>
        </Tooltip>
        {showTutorialPanel && (
          <div className="tutorial-panel">
            <div className="tutorial-panel-header">
              <h3>Tutorials</h3>
              <button
                className="close-panel-button"
                onClick={() => setShowTutorialPanel(false)}
              >
                <X size={18} />
              </button>
            </div>
            {isLoadingTutorials ? (
              <div className="loading-state">Loading tutorials...</div>
            ) : tutorialsError ? (
              <div className="error-state">{tutorialsError}</div>
            ) : (
              <ul className="tutorial-list">
                {tutorials.map((tutorial) => (
                  <li
                    key={tutorial._id || tutorial.id}
                    className="tutorial-list-item"
                    onClick={() => setSelectedTutorial(tutorial)}
                  >
                    <div className="tutorial-list-content">
                      <h4>{tutorial.title}</h4>
                      <p>{tutorial.description}</p>
                      <span className="tutorial-duration">
                        {tutorial.readTime}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {selectedTutorial && (
          <TutorialModal
            tutorial={selectedTutorial}
            onClose={() => {
              setSelectedTutorial(null);
            }}
            showExitMessage={showExitMessage}
            setShowExitMessage={setShowExitMessage}
            handleSkip={handleSkip}
            canSkipVideo={canSkipVideo}
            progressFill={loadingProgress}
            progressLabel={phase.label}
            showProgress={false}
          />
        )}
      </>
    );
  }

  return (
    <div className="loading-screen">
      <div className="loading-content">
        {hasLoggedInOnce ? (
          <div className="please-wait-panel">
            <div className="wait-header">
              <Activity size={18} className="loading-icon animating" />
              <h3>Please wait</h3>
            </div>
            <p>Reconnecting...</p>
            {latestArcwareText && (
              <p className="arcware-status-line">{latestArcwareText}</p>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 12, padding: "8px 20px", borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)", background: "transparent",
                color: "rgba(255,255,255,0.5)", fontSize: 13, cursor: "pointer",
              }}
            >
              Refresh Page
            </button>
          </div>
        ) : viewMode === "choice" ? (
          <div className="mode-choice">
            <div className="selector-header">
              <h2>What would you like to do while waiting?</h2>
            </div>
            <div className="mode-choice-buttons">
              <button className="mode-btn" onClick={() => setViewMode("tutorials")}>
                <BookOpen size={28} />
                <span>Tutorials</span>
                <p>Click-through guides</p>
              </button>
              <button className="mode-btn" onClick={() => setViewMode("tips")}>
                <Lightbulb size={28} />
                <span>Tips &amp; Tricks</span>
                <p>Quick tips while you wait</p>
              </button>
            </div>
          </div>
        ) : viewMode === "tutorials" ? (
          selectedTutorial ? (
            <>
              {/* <div className="back-header">
                <button
                  onClick={() => setSelectedTutorial(null)}
                  className="back-button"
                >
                  <ChevronLeft size={16} />
                  <span>Back to tutorials</span>
                </button>
              </div> */}
              <TutorialModal
                tutorial={selectedTutorial}
                onClose={() => setSelectedTutorial(null)}
                showExitMessage={showExitMessage}
                setShowExitMessage={setShowExitMessage}
                handleSkip={handleSkip}
                canSkipVideo={canSkipVideo && !loginPending}
                progressFill={loadingProgress}
                progressLabel={phase.label}
              />
            </>
          ) : (
            <div className="tutorial-selector">
              <div className="selector-header">
                <button
                  className="back-button"
                  onClick={() => setViewMode("choice")}
                >
                  <ChevronLeft size={16} />
                  <span>Back</span>
                </button>
                <h2>Select a tutorial</h2>
                <p>Click-through guides while your application loads</p>
              </div>
              {isLoadingTutorials ? (
                <div className="loading-state">Loading tutorials...</div>
              ) : tutorialsError ? (
                <div className="error-state">{tutorialsError}</div>
              ) : (
                <div className="tutorial-grid">
                  {tutorials.map((tutorial) => (
                    <div
                      key={tutorial._id || tutorial.id}
                      className="tutorial-card"
                      onClick={() => setSelectedTutorial(tutorial)}
                    >
                      <div className="card-content">
                        <h3>{tutorial.title}</h3>
                        <p>{tutorial.description}</p>
                        <div className="card-footer">
                          <span className="tutorial-duration">
                            {tutorial.readTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        ) : viewMode === "tips" ? (
          <div className="tips-view">
            <div className="tips-view-header">
              <button
                className="back-button"
                onClick={() => { setViewMode("choice"); setCurrentTipIndex(Math.floor(Math.random() * TIPS.length)); }}
              >
                <ChevronLeft size={16} />
                <span>Back</span>
              </button>
              <p className="tips-caption">{TIPS[currentTipIndex].caption}</p>
              <button
                className="next-tip-btn"
                onClick={() =>
                  setCurrentTipIndex((i) => getNextTipIndex(i, TIPS.length))
                }
              >
                Next tip →
              </button>
            </div>
            <div className="tips-media">
              <img
                key={TIPS[currentTipIndex].src}
                src={TIPS[currentTipIndex].src}
                alt={TIPS[currentTipIndex].caption}
                className="tips-gif"
              />
            </div>
          </div>
        ) : null}

        <div
          className="loading-progress"
          style={{ display: hasLoggedInOnce ? "none" : "block" }}
        >
          {/* Direct login (?action=login): the credentials are already validated and
              submit themselves once the app is ready, so the user needs reassurance —
              not a curtain over the status they're waiting on. */}
          {loginPending && (
            <div className="login-pending-banner">
              <Activity size={16} className="loading-icon animating" />
              <span>
                Signing you in automatically. No action needed. Your session will begin as soon as everything is ready.
              </span>
            </div>
          )}
          <div className="progress-info">
            <div className="status-indicator">
              <Activity
                size={16}
                className={`loading-icon ${canSkipVideo ? "completed" : "animating"
                  }`}
              />
              <span>{statusLabel}</span>
            </div>
            <span className="percentage">{Math.floor(loadingProgress)}%</span>
          </div>
          <div className="progress-track">
            <div
              className={`progress-bar ${canSkipVideo ? "" : "working"}`}
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
          {/* Show the "still connecting" banner once loading has run past ~70s
              (LOADING_TIMEOUT_SEC is 90, so loadingTimeLeft <= 20). Servers have
              been starting in 40-60s, so the banner must not fire inside that
              window. The refresh modal takes over at 90s elapsed (loadingTimeLeft
              === 0). */}
          {!hasLoggedInOnce && !canSkipVideo && loadingTimeLeft <= 20 && (
            <div style={{
              marginTop: 12,
              padding: "12px 16px",
              borderRadius: 8,
              backgroundColor: "rgba(239,68,68,0.2)",
              border: "1px solid rgba(239,68,68,0.55)",
              color: "#fca5a5",
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.5,
              textAlign: "center",
            }}>
              Still connecting — this is taking a little longer than usual. You can refresh if this continues.
            </div>
          )}
          {/* No manual Continue while a login is submitting itself: taking it would
              dismiss this screen mid-login and leave the user on a stream they aren't
              signed into yet. */}
          {canSkipVideo && !loginPending && (
            <button className="skip-button pulse" onClick={handleSkip}>
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoadingScreen;
